import React from 'react';
import { parse } from 'date-fns';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Message from 'components/Message';
import { PERMISSION } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { REPORT_TYPES } from 'features/Stats/Stats.constants';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectUserHasPermission } from 'selectors/general';

import SegmentedCapacityBar, {
  SEGMENTED_CAPACITY_BAR_SEGMENT,
  SegmentedCapacityBarLegend,
} from '../Components/SegmentedCapacityBar';
import { getScheduleListV2FillBar } from '../ScheduleListV2/ScheduleListV2.helpers';
import { useScheduleInsights } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';
import './HistoricalUtilization.scss';

const { block, element } = bem('historical-utilization-v2');

const EARNED_EMPTY_AMOUNT = 0;
const FILL_BAR_EMPTY_COUNT = 0;
const HISTORY_DATE_FORMAT = 'yyyy-MM-dd';
const SEGMENT_VIEW_OTHER_CLASSES_CLICKED = 'view_other_classes_clicked';
const TOOLTIP_NAME_PREFIX = 'historical-utilization';
const VIEW_OTHER_CLASSES_PATH = makePath.reports({ reportType: REPORT_TYPES.CLASSES });

const SEGMENT_TOOLTIP_MESSAGES = {
  [SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS]: messages.tooltipClasspass,
  [SEGMENTED_CAPACITY_BAR_SEGMENT.DIRECT]: messages.tooltipDirect,
  [SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED]: messages.tooltipUnfilled,
};

function getHistoryFillBar(historyEntry, hasIntegrationData) {
  return getScheduleListV2FillBar({
    hasIntegrationData,
    spots: {
      classpassBooked: historyEntry.totalReservationsClasspass,
      classpassTotal: historyEntry.networkCap,
      maxCapacity: historyEntry.maxCapacity,
      totalBooked: historyEntry.totalReservations,
    },
  });
}

function getLegendItems(hasIntegrationData) {
  return [
    hasIntegrationData && {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.DIRECT,
      label: <Message message={messages.legendDirectOnly} />,
    },
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS,
      label: <Message message={hasIntegrationData ? messages.legendClasspassOnly : messages.legendClasspassBookings} />,
    },
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED,
      label: <Message message={messages.legendUnfilled} />,
    },
  ].filter(Boolean);
}

function withSegmentTooltips(segments) {
  return segments.map(segment => {
    const tooltipMessage = SEGMENT_TOOLTIP_MESSAGES[segment.key];

    return {
      ...segment,
      tooltip: tooltipMessage ? <Message message={tooltipMessage} values={{ count: segment.value }} /> : null,
    };
  });
}

function UtilizationCell({ barName, fillBar }) {
  const trackTotal = fillBar.segments.reduce((sum, { value }) => sum + value, FILL_BAR_EMPTY_COUNT);

  if (trackTotal === FILL_BAR_EMPTY_COUNT) {
    return <Message className={element('empty')} message={messages.emptyUtilization} />;
  }

  return (
    <div className={element('graph')}>
      <SegmentedCapacityBar name={barName} segments={withSegmentTooltips(fillBar.segments)} />
      <div className={element('axis')}>
        <span className={element('tick', { start: true })}>{FILL_BAR_EMPTY_COUNT}</span>
        <span className={element('tick', { end: true })}>{trackTotal}</span>
      </div>
    </div>
  );
}

UtilizationCell.propTypes = {
  barName: PropTypes.string.isRequired,
  fillBar: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

const HistoricalUtilization = ({ schedule }) => {
  const { currencyCode, directFillDataDisabled, isManualStudio, venueId } = useSelector(selectActiveVenue);
  const canViewRates = useSelector(state => selectUserHasPermission(state, PERMISSION.DISPLAY_RATE));
  const { data: insights, isError, isLoading } = useScheduleInsights(schedule.scheduleId);
  const history = insights?.history || [];
  const hasIntegrationData = !(isManualStudio || directFillDataDisabled);

  const handleViewOtherClassesClick = () => {
    Segment.event(SEGMENT_VIEW_OTHER_CLASSES_CLICKED, { venueId, scheduleId: schedule.scheduleId });
  };

  if (isLoading || isError || !history.length) {
    return null;
  }

  return (
    <section className={block()} data-cypress="schedule-detail-v2-historical-utilization">
      <Message as="h3" className={element('title')} message={messages.title} />
      <Message
        as="p"
        className={element('summary')}
        message={messages.summary}
        values={{
          className: schedule.scheduleName,
          startDateTime: schedule.startDateTime,
          weeksCount: history.length,
        }}
      />
      <SegmentedCapacityBarLegend items={getLegendItems(hasIntegrationData)} />
      <table className={element('table')}>
        <thead>
          <tr>
            <Message as="th" className={element('header-cell')} message={messages.date} />
            <Message as="th" className={element('header-cell')} message={messages.utilization} />
            {canViewRates && (
              <Message as="th" className={element('header-cell', { earned: true })} message={messages.earned} />
            )}
          </tr>
        </thead>
        <tbody>
          {history.map((historyEntry, index) => {
            const { payouts: { totalRevenue } = {}, scheduleDate } = historyEntry;
            const date = parse(scheduleDate, HISTORY_DATE_FORMAT, new Date());
            const fillBar = getHistoryFillBar(historyEntry, hasIntegrationData);
            const rowKey = `${scheduleDate}-${index}`;

            return (
              <tr className={element('body-row')} key={rowKey}>
                <td className={element('cell', { date: true })}>
                  <Message className={element('date', { mobile: true })} format="short-w-2-digit-year" value={date} />
                  <Message className={element('date', { desktop: true })} format="medium" value={date} />
                </td>
                <td className={element('cell', { utilization: true })}>
                  <UtilizationCell barName={`${TOOLTIP_NAME_PREFIX}-${rowKey}`} fillBar={fillBar} />
                </td>
                {canViewRates && (
                  <td className={element('cell', { earned: true })}>
                    <Message
                      currency={currencyCode}
                      format="currency-short"
                      value={totalRevenue ?? EARNED_EMPTY_AMOUNT}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={element('footer')}>
        <Message
          as={Link}
          className={element('link')}
          message={messages.viewOtherClasses}
          onClick={handleViewOtherClassesClick}
          to={VIEW_OTHER_CLASSES_PATH}
        />
      </div>
    </section>
  );
};

HistoricalUtilization.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

export default HistoricalUtilization;
